import type { CatalogProposal, CatalogView } from "./catalogNetworkModel.ts";

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (text !== undefined) element.textContent = text;
  return element;
}
function field(form: HTMLFormElement, label: string, name: string, value = "", type = "text") {
  const l = node("label", label),
    input = node("input");
  input.name = name;
  input.type = type;
  input.value = value;
  if (type === "text") input.inputMode = "text";
  input.required = !["brand", "upc"].includes(name);
  l.append(input);
  form.append(l);
  return input;
}
function select(
  form: HTMLFormElement,
  label: string,
  name: string,
  options: readonly { id: string; label: string }[],
  value = "",
) {
  const l = node("label", label),
    s = node("select");
  s.name = name;
  s.required = true;
  s.append(new Option("Choose", ""));
  for (const o of options) s.append(new Option(o.label, o.id));
  s.value = value;
  l.append(s);
  form.append(l);
  return s;
}

export function appendCatalogProductFields(
  f: HTMLFormElement,
  view: CatalogView,
  p?: CatalogProposal,
  query = "",
): void {
  field(f, "Name", "name", p?.name ?? query);
  field(f, "Brand", "brand", p?.brand ?? "");
  const kind = select(
    f,
    "Item type",
    "kindId",
    view.kinds.filter((k) => k.active).map((k) => ({ id: k.id, label: k.label })),
    p?.kindId,
  );
  const category = select(
    f,
    "Category",
    "categoryId",
    view.categories
      .filter((c) => c.active && c.kind_id === kind.value)
      .map((c) => ({ id: c.id, label: c.label })),
    p?.categoryId,
  );
  kind.onchange = () => {
    category.replaceChildren(new Option("Choose", ""));
    for (const c of view?.categories ?? [])
      if (c.active && c.kind_id === kind.value) category.append(new Option(c.label, c.id));
  };
  select(
    f,
    "Package",
    "packageClass",
    ["bottle", "can", "keg", "pouch", "container", "multipack"].map((id) => ({ id, label: id })),
    p?.packageClass,
  );
  const size = field(f, "Size", "sizeValue", p?.sizeValue ?? "");
  size.inputMode = "decimal";
  size.pattern = "(?:[1-9][0-9]{0,7}|0)(?:\\.[0-9]{1,4})?";
  select(
    f,
    "Size unit",
    "sizeUnit",
    ["ml", "l", "oz", "g", "kg", "each"].map((id) => ({ id, label: id })),
    p?.sizeUnit,
  );
  const units = field(
    f,
    "Units per package",
    "unitsPerPackage",
    String(p?.unitsPerPackage ?? 1),
    "number",
  );
  units.min = "1";
  units.max = "10000";
  units.step = "1";
  const upc = field(f, "UPC", "upc", p?.upc ?? "");
  upc.inputMode = "numeric";
  upc.pattern = "[0-9]{8,14}";
}

export function readCatalogProductFields(f: HTMLFormElement): CatalogProposal {
  const d = new FormData(f);
  return {
    name: String(d.get("name")),
    brand: String(d.get("brand")) || null,
    kindId: String(d.get("kindId")),
    categoryId: String(d.get("categoryId")),
    packageClass: String(d.get("packageClass")),
    sizeValue: String(d.get("sizeValue")),
    sizeUnit: String(d.get("sizeUnit")),
    unitsPerPackage: Number(d.get("unitsPerPackage")),
    upc: String(d.get("upc")) || null,
  };
}
